import { debounce } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAssignedAgentsQuery } from "web_myplann/Api/Advisor/useAssignedAgentsQuery";
import { useChatListQuery } from "web_myplann/Api/Chat/useChatListQuery";
import {
	type ConversationListItemResponseDTO,
	UserAgentStatusEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { useOnlineStatus } from "web_myplann/Infrastructure/Hooks/useOnlineStatus";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

const R = Resources.Chat;

const SEARCH_DEBOUNCE_MS = 300;

export const useChatListPage = () => {
	const navigate = useNavigate();
	const { t } = useResource();
	const isOnline = useOnlineStatus();
	const [query, setQuery] = useState("");
	const [search, setSearch] = useState("");
	const [pickerOpen, setPickerOpen] = useState(false);

	const searchActive = query.trim().length > 0;

	const debouncedSetSearch = useMemo(
		() => debounce(setSearch, SEARCH_DEBOUNCE_MS),
		[],
	);

	useEffect(() => {
		debouncedSetSearch(query.trim());

		return () => debouncedSetSearch.cancel();
	}, [query, debouncedSetSearch]);

	const {
		data: conversations = [],
		isLoading,
		isPlaceholderData,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	} = useChatListQuery(search);
	const { data: advisors, isLoading: isLoadingAdvisors } =
		useAssignedAgentsQuery(UserAgentStatusEnum.Connected);

	const recipientsWithoutChat = useMemo(
		() => (advisors?.data ?? []).filter((advisor) => !advisor.conversationId),
		[advisors],
	);

	const openConversation = (conversation: ConversationListItemResponseDTO) => {
		navigate(Routing.Chat.Detail.path(String(conversation.id)));
	};

	const startConversation = (advisorId: string) => {
		setPickerOpen(false);
		navigate(Routing.Chat.Detail.path("new", advisorId));
	};

	const openNewMessage = () => {
		const [firstRecipient, ...otherRecipients] = recipientsWithoutChat;
		if (!firstRecipient) return;

		if (otherRecipients.length === 0) {
			startConversation(firstRecipient.uid);
			return;
		}

		setPickerOpen(true);
	};

	const clearSearch = () => setQuery("");

	const getPlaceholder = () => {
		if (!isOnline) return { title: t(R.offline) };

		if (searchActive)
			return {
				title: t(R.searchEmptyTitle),
				description: t(R.searchEmptyDescription),
				actionLabel: t(R.clearSearch),
				onAction: clearSearch,
			};

		return {
			title: t(R.listEmptyTitle),
			description:
				recipientsWithoutChat.length > 1
					? t(R.listEmptyDescriptionTeam)
					: t(R.listEmptyDescription),
			...(recipientsWithoutChat.length > 0 && {
				actionLabel: t(R.writeToAdvisor),
				onAction: openNewMessage,
			}),
		};
	};

	return {
		items: isOnline ? conversations : [],
		isLoading: isLoading || isLoadingAdvisors,
		hasMore: isOnline && hasNextPage && !isPlaceholderData,
		isLoadingMore: isFetchingNextPage,
		loadMore: () => fetchNextPage(),
		query,
		setQuery,
		placeholder: getPlaceholder(),
		isSearchable: isOnline,
		openConversation,
		recipientsWithoutChat,
		canStartNewMessage: isOnline && recipientsWithoutChat.length > 0,
		openNewMessage,
		pickerOpen,
		closePicker: () => setPickerOpen(false),
		startConversation,
	};
};
